import { LayoutTemplate, Network } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/Components/ui/tabs";
import { Count } from "./primitives";

/**
 * The passport page's underline tab strip (C1): Overview, Import, Changelog
 * (with its entry count), Data requests (with the open count), and, on a root
 * passport, Ontology and Composer after a divider.
 *
 * Two ways to place it:
 * - Inside a Radix `<Tabs>` root that also holds the panels (the editor, whose
 *   root owns the value, the `?tab=` URL and the TabsContent the triggers
 *   point at). Leave `value` out.
 * - On its own (a vignette): pass `value` (and `onChange(value)`), and it
 *   brings its own root.
 *
 * - `showImport`: the level has `series`/`many` children to import.
 * - `changelogCount`, `changelogHasMore`: entries loaded, and whether older
 *   ones exist (the count reads "50+").
 * - `showDataRequests`, `openRequests`: the Data requests tab, and its amber
 *   "N open" badge when above zero.
 * - `showSchemaTabs`: the root passport's Ontology and Composer tabs.
 */
export default function PassportTabs({
    value,
    onChange,
    showImport = false,
    changelogCount = 0,
    changelogHasMore = false,
    showDataRequests = false,
    openRequests = null,
    showSchemaTabs = false,
}) {
    const strip = (
        <TabsList variant="underline" indicator className="mt-4">
            <TabsTrigger value='overview'>Overview</TabsTrigger>
            {showImport && <TabsTrigger value='import'>Import</TabsTrigger>}
            <TabsTrigger value='logs'>
                Changelog
                {changelogCount > 0 && <Count>{changelogCount}{changelogHasMore ? '+' : ''}</Count>}
            </TabsTrigger>
            {showDataRequests && (
                <TabsTrigger value='data_requests'>
                    Data requests
                    {openRequests > 0 && <Count tone="amber">{openRequests} open</Count>}
                </TabsTrigger>
            )}
            {showSchemaTabs && <span className="h-4 w-px shrink-0 bg-zinc-200" aria-hidden="true" />}
            {showSchemaTabs && (
                <TabsTrigger value='ontology' title="Edits the schema of every passport in this ontology">
                    <Network className="size-3.5 text-zinc-400" aria-hidden="true" />Ontology
                </TabsTrigger>
            )}
            {showSchemaTabs && (
                <TabsTrigger value='composer' title="Edits the public page of every passport in this ontology">
                    <LayoutTemplate className="size-3.5 text-zinc-400" aria-hidden="true" />Composer
                </TabsTrigger>
            )}
        </TabsList>
    );

    if (value === undefined) {
        return strip;
    }

    return <Tabs value={value} onValueChange={onChange}>{strip}</Tabs>;
}
